import React from 'react';
import { getPrivateCacheScope, subscribePrivateCacheScope } from './privateCacheScope.js';

const CACHE_PREFIX = 'photo-variants-v7:';
const MANIFEST_PREFIX = 'photoCacheManifest:v6:';
const cacheName = () => `${CACHE_PREFIX}${getPrivateCacheScope().scope}`;
const manifestKey = () => `${MANIFEST_PREFIX}${getPrivateCacheScope().scope}`;
const BUDGET_BYTES = 256 * 1024 * 1024; // ~256MB budget for variants
const MAX_ENTRIES = 1200;
const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

const variantWeight = { xl: 4, lg: 3, md: 2, sm: 1 };

const supportsCaches = () => typeof window !== 'undefined' && 'caches' in window;
const supportsStorage = () => typeof window !== 'undefined' && typeof localStorage !== 'undefined';
const crossOriginLoaded = new Set();
const crossOriginLoading = new Map();
const variantLoads = new Map();
let manifestWrites = Promise.resolve();
let cacheGeneration = 0;
subscribePrivateCacheScope(() => {
  cacheGeneration++;
  variantLoads.clear();
  crossOriginLoaded.clear();
  crossOriginLoading.clear();
});

// Cache responses and their manifest must commit together. Fetches stay parallel.
function writeManifest(task) {
  const generation = cacheGeneration;
  const guarded = () => generation === cacheGeneration && getPrivateCacheScope().scope ? task() : undefined;
  const result = manifestWrites.then(() => typeof navigator !== 'undefined' && navigator.locks
    ? navigator.locks.request(`photo-variant-manifest:${getPrivateCacheScope().scope}`, guarded) : guarded());
  manifestWrites = result.catch(() => {});
  return result;
}

function isCrossOrigin(url) {
  try {
    const target = new URL(url, window.location.origin);
    return typeof window !== 'undefined' && target.origin !== window.location.origin;
  } catch {
    return false;
  }
}

function preloadCrossOrigin(url) {
  if (!url || typeof window === 'undefined') return Promise.resolve();
  if (crossOriginLoaded.has(url)) return Promise.resolve();
  if (crossOriginLoading.has(url)) return crossOriginLoading.get(url);

  const promise = new Promise((resolve) => {
    const img = new Image();
    img.decoding = 'async';
    img.fetchPriority = 'low';
    img.onload = () => {
      crossOriginLoaded.add(url);
      resolve();
    };
    img.onerror = () => {
      resolve();
    };
    img.src = url;
  }).finally(() => {
    crossOriginLoading.delete(url);
  });

  crossOriginLoading.set(url, promise);
  return promise;
}

function safeParse(json) {
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function loadManifest() {
  if (!supportsStorage() || !getPrivateCacheScope().scope) return [];
  try {
    const parsed = safeParse(window.localStorage.getItem(manifestKey()));
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveManifest(manifest) {
  if (!supportsStorage()) return;
  try {
    window.localStorage.setItem(manifestKey(), JSON.stringify(manifest));
  } catch {
    // Swallow quota errors; pruning will run on next sweep
  }
}

let _oldCachesCleaned = false;
async function openCache() {
  if (!supportsCaches() || !getPrivateCacheScope().scope) return null;
  try {
    const cache = await caches.open(cacheName());
    // Purge old photo-variants-* caches once per session
    if (!_oldCachesCleaned) {
      _oldCachesCleaned = true;
      caches.keys().then(names => {
        names.forEach(name => {
          if (name.startsWith('photo-variants-') && !name.startsWith(CACHE_PREFIX)) {
            caches.delete(name);
          }
        });
      }).catch(() => {});
    }
    return cache;
  } catch {
    return null;
  }
}

export function getPhotoVersion(image) {
  return image?._v
    || image?.updated_at
    || image?.updatedAt
    || image?.updated
    || image?.modified_at
    || image?.id
    || image?.filename;
}

// Helper to determine if we should show the original instead of a variant
function shouldShowOriginal(image, variant) {
  // Premium mode: always prefer generated variants over originals.
  return false;
}

export function buildVariantUrl(image, variant = 'sm') {
  if (!image) return null;
  const forceOrigin = Boolean(image?._force_origin || image?.origin_only || image?.force_origin);
  const version = getPhotoVersion(image);

  const appendVersionParam = (url, version) => {
    if (!url || !version) return url;
    const encoded = encodeURIComponent(String(version));
    const joiner = url.includes('?') ? '&' : '?';
    return `${url}${joiner}v=${encoded}`;
  };
  const canAppendToCdn = (url) => {
    if (!url) return false;
    const lower = url.toLowerCase();
    // Don't modify presigned S3/R2 URLs - adding params invalidates the signature
    if (lower.includes('x-amz-signature')) return false;
    return !lower.includes('token=') && !lower.includes('expires=');
  };

  // Use CDN URLs directly if available (bypasses origin server)
  if (!forceOrigin && image.cdn_urls) {
    const cdnUrl = image.cdn_urls[variant];
    if (cdnUrl) {
      return canAppendToCdn(cdnUrl) ? appendVersionParam(cdnUrl, version) : cdnUrl;
    }
    // A partial thumbnail payload is not a full-size preview. Request missing
    // sizes from the authenticated origin rather than stretching the thumbnail.
  }

  // Fallback to origin server URLs
  const rawName = image.filename || image.thumbnail_url;
  if (!rawName) return null;
  const cleaned = rawName.split('?')[0];
  const name = cleaned.includes('/') ? cleaned.split('/').pop() : cleaned;
  if (!name) return null;
  const tokenParam = ''; // Private media uses the authenticated same-origin session cookie.
  const versionParam = version ? `${tokenParam ? '&' : '?'}v=${encodeURIComponent(String(version))}` : '';

  // For large variants, serve original if it's a small JPEG
  if (shouldShowOriginal(image, variant)) {
    console.log(`[photoCache] ${name}: serving ORIGINAL (${((image.bytes || 0) / 1024 / 1024).toFixed(1)}MB JPEG)`);
    return `/media/originals/${name}${tokenParam}${versionParam}`;
  }

  // Otherwise use the variant
  const base = name.replace(/\.[^.]+$/, '.jpg');
  return `/media/variants/${variant}/${base}${tokenParam}${versionParam}`;
}

function revokeObjectUrl(url) {
  if (url && url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
}

async function pruneManifest(manifest, cache) {
  if (!cache) return manifest;

  const cutoff = Date.now() - MAX_AGE_MS;
  const expired = manifest.filter((entry) => !entry.lastAccessed || entry.lastAccessed < cutoff);
  for (const entry of expired) {
    try { await cache.delete(entry.cacheKey); } catch {}
  }
  let filtered = manifest.filter((entry) => entry.lastAccessed && entry.lastAccessed >= cutoff);
  let total = filtered.reduce((sum, entry) => sum + (entry.size || 0), 0);

  // Evict larger variants first, then least recently used
  filtered.sort((a, b) => {
    const weightDelta = (variantWeight[b.variant] || 0) - (variantWeight[a.variant] || 0);
    if (weightDelta !== 0) return weightDelta;
    return (a.lastAccessed || 0) - (b.lastAccessed || 0);
  });

  while ((total > BUDGET_BYTES || filtered.length > MAX_ENTRIES) && filtered.length > 0) {
    const victim = filtered.shift();
    if (!victim) break;
    total -= victim.size || 0;
    try {
      await cache.delete(victim.cacheKey);
    } catch {
      // Ignore cache deletion errors
    }
  }

  return filtered;
}

async function putAndRecord({ cache, cacheKey, response, image, variant, signature, stableKey, generation }) {
  const blob = await response.clone().blob();
  await writeManifest(async () => {
    if (generation !== cacheGeneration) return;
    // Never advertise a cache entry when the write failed (quota/private mode).
    await cache.put(cacheKey, response.clone());
    if (generation !== cacheGeneration) return;
    const entry = {
      cacheKey, stableKey, id: image?.id, filename: image?.filename,
      variant, signature, etag: response.headers.get('ETag'),
      size: blob.size, lastAccessed: Date.now()
    };
    const manifest = loadManifest().filter(e => (e.stableKey || e.cacheKey) !== stableKey);
    const pruned = await pruneManifest([...manifest, entry], cache);
    if (generation === cacheGeneration) saveManifest(pruned);
  }).catch(() => {});
  return blob;
}

// Build a STABLE cache key without version params (for cache storage lookup)
function buildStableCacheKey(image, variant) {
  if (!image) return null;
  const rawName = image.filename || image.thumbnail_url;
  if (!rawName) return null;
  const cleaned = rawName.split('?')[0];
  const name = cleaned.includes('/') ? cleaned.split('/').pop() : cleaned;
  if (!name) return null;
  const base = name.replace(/\.[^.]+$/, '.jpg');
  // No token or version params - just the base path for stable caching
  return `/__private_photo_cache/${getPrivateCacheScope().scope}/${variant}/${base}`;
}

async function loadVariant(image, variant, { allowNetwork = true, generation = cacheGeneration } = {}) {
  const displayUrl = buildVariantUrl(image, variant); // URL with auth token for actual fetching
  if (!displayUrl) return null;

  // Use stable key (without version/token) for cache storage
  const stableCacheKey = buildStableCacheKey(image, variant);
  if (!stableCacheKey) return displayUrl;

  const crossOrigin = isCrossOrigin(displayUrl);

  // Skip CacheStorage for cross-origin CDN URLs - let browser handle
  if (crossOrigin) {
    return displayUrl;
  }

  const cache = await openCache();
  if (generation !== cacheGeneration) return null;
  if (!cache) {
    return displayUrl;
  }

  const manifest = loadManifest();
  const signature = getPhotoVersion(image);
  // Look up by stable key (image + variant), not the full versioned URL
  const existingIdx = manifest.findIndex((e) => e.stableKey === stableCacheKey || e.cacheKey === stableCacheKey);
  const existing = existingIdx >= 0 ? manifest[existingIdx] : null;
  const cachedResponse = await cache.match(stableCacheKey);
  const fresh = existing && cachedResponse && existing.signature === signature;

  if (fresh) {
    // Cache hit! Serve from local cache instantly
    if (typeof window !== 'undefined' && window.__PHOTO_CACHE_DEBUG__) {
      console.log('[PhotoCache] HIT', variant, image?.filename?.substring(0, 30));
    }
    await writeManifest(() => {
      saveManifest(loadManifest().map(entry => (entry.stableKey || entry.cacheKey) === stableCacheKey
        ? { ...entry, lastAccessed: Date.now() } : entry));
    });
    try {
      const blob = await cachedResponse.clone().blob();
      return blob;
    } catch {
      return displayUrl;
    }
  }

  if (!allowNetwork) {
    // Can't fetch, return cached URL if available or null
    if (cachedResponse) {
      try {
        const blob = await cachedResponse.clone().blob();
        return blob;
      } catch {
        return displayUrl;
      }
    }
    return null;
  }

  if (typeof window !== 'undefined' && window.__PHOTO_CACHE_DEBUG__) {
    console.log('[PhotoCache] MISS', variant, image?.filename?.substring(0, 30), existing ? '(stale)' : '(new)');
  }

  const headers = {};
  if (existing?.etag) {
    headers['If-None-Match'] = existing.etag;
  }

  const fetchOptions = { mode: 'same-origin', credentials: 'include' };

  let response;
  try {
    response = await fetch(displayUrl, {
      ...fetchOptions,
      headers
    });
  } catch {
    // Network error - serve stale if available
    if (cachedResponse) {
      try {
        const blob = await cachedResponse.clone().blob();
        return blob;
      } catch {}
    }
    return displayUrl;
  }

  // If 304 but we somehow lack the body, refetch without conditional headers
  if (response.status === 304 && !cachedResponse) {
    try {
      response = await fetch(displayUrl, fetchOptions);
    } catch {
      return displayUrl;
    }
  }

  // Reuse cached body on 304 (server confirmed content unchanged)
  if (response.status === 304 && cachedResponse) {
    if (typeof window !== 'undefined' && window.__PHOTO_CACHE_DEBUG__) {
      console.log('[PhotoCache] 304', variant, image?.filename?.substring(0, 30));
    }
    await writeManifest(() => {
      saveManifest(loadManifest().map(entry => (entry.stableKey || entry.cacheKey) === stableCacheKey
        ? { ...entry, signature, lastAccessed: Date.now() } : entry));
    });
    try {
      const blob = await cachedResponse.clone().blob();
      return blob;
    } catch {
      return displayUrl;
    }
  }

  if (!response.ok) {
    // Serve stale on error if available
    if (cachedResponse) {
      try {
        const blob = await cachedResponse.clone().blob();
        return blob;
      } catch {}
    }
    return displayUrl;
  }

  // Don't cache SVG placeholders (processing/variant-missing) — they're temporary
  const ct = (response.headers.get('content-type') || '').toLowerCase();
  if (ct.includes('svg')) {
    return displayUrl;
  }

  const result = await putAndRecord({
    cache,
    cacheKey: stableCacheKey,
    response,
    image,
    variant,
    signature,
    stableKey: stableCacheKey,
    generation,
  });

  return result || displayUrl;
}

// Share the bytes, never a blob URL: each consumer owns and revokes its own URL.
export async function getVariantUrl(image, variant = 'sm', options = {}) {
  const { returnObjectUrl = true, allowNetwork = true } = options;
  const displayUrl = buildVariantUrl(image, variant);
  if (!displayUrl) return null;
  if (isCrossOrigin(displayUrl)) {
    if (!returnObjectUrl && allowNetwork) await preloadCrossOrigin(displayUrl);
    return displayUrl;
  }
  const generation = cacheGeneration;
  const key = `${displayUrl}:${allowNetwork}:${generation}`;
  let pending = variantLoads.get(key);
  if (!pending) {
    pending = loadVariant(image, variant, { allowNetwork, generation }).catch(() => displayUrl);
    variantLoads.set(key, pending);
    pending.finally(() => {
      if (variantLoads.get(key) === pending) variantLoads.delete(key);
    });
  }
  const value = await pending;
  if (generation !== cacheGeneration) return null;
  if (value === null) return null;
  return returnObjectUrl && value instanceof Blob ? URL.createObjectURL(value) : (typeof value === 'string' ? value : displayUrl);
}

function createQueueRunner(tasks, concurrency = 3) {
  let active = 0;
  let index = 0;

  return new Promise((resolve) => {
    const next = () => {
      if (index >= tasks.length && active === 0) {
        resolve();
        return;
      }

      while (active < concurrency && index < tasks.length) {
        const task = tasks[index++];
        active++;
        task()
          .catch(() => { })
          .finally(() => {
            active--;
            next();
          });
      }
    };

    next();
  });
}

export async function prefetchVariants(images, options = {}) {
  const {
    variants = ['sm', 'md'],
    warmLargeCount = 0,
    limit,
    concurrency = 3
  } = options;

  const list = Array.isArray(images) ? images : [];
  const bounded = typeof limit === 'number' ? list.slice(0, limit) : list;
  const tasks = [];

  bounded.forEach((img) => {
    variants.forEach((variant) => {
      const url = buildVariantUrl(img, variant);
      if (!url) return;
      tasks.push(() => getVariantUrl(img, variant, { returnObjectUrl: false }));
    });
  });

  if (warmLargeCount > 0) {
    bounded.slice(0, warmLargeCount).forEach((img) => {
      const url = buildVariantUrl(img, 'xl');
      if (!url) return;
      tasks.push(() => getVariantUrl(img, 'xl', { returnObjectUrl: false }));
    });
  }

  if (tasks.length === 0) return;
  await createQueueRunner(tasks, concurrency);
}

export async function evictByFilenames(filenames = []) {
  const generation = ++cacheGeneration;
  return writeManifest(async () => {
    if (!filenames || filenames.length === 0) return;
    const cache = await openCache();
    if (!cache || generation !== cacheGeneration) return;

    const manifest = loadManifest();
    const targets = new Set(filenames.map((f) => f && f.replace(/\.[^.]+$/, '.jpg')));
    const remaining = [];

    for (const entry of manifest) {
      const matches = entry.filename && targets.has(entry.filename.replace(/\.[^.]+$/, '.jpg'));
      if (matches) {
        try {
          await cache.delete(entry.cacheKey);
        } catch { }
      } else {
        remaining.push(entry);
      }
    }

    if (generation === cacheGeneration) saveManifest(remaining);
  });
}

export async function evictImage(image) {
  const generation = ++cacheGeneration;
  return writeManifest(async () => {
    if (!image) return;
    const cache = await openCache();
    if (!cache || generation !== cacheGeneration) return;

    const manifest = loadManifest();
    const filename = typeof image === 'string' ? image : image.filename;
    const normalized = filename ? filename.replace(/\.[^.]+$/, '.jpg') : null;
    const targetId = typeof image === 'object' ? image.id : null;

    const remaining = [];

    for (const entry of manifest) {
      const entryName = entry.filename ? entry.filename.replace(/\.[^.]+$/, '.jpg') : null;
      const matches = (targetId && entry.id === targetId) || (normalized && entryName === normalized);
      if (matches) {
        try {
          await cache.delete(entry.cacheKey);
        } catch { }
      } else {
        remaining.push(entry);
      }
    }

    if (generation === cacheGeneration) saveManifest(remaining);
  });
}

export async function clearAllVariants() {
  cacheGeneration++;
  const oldCacheName = cacheName();
  const oldManifestKey = manifestKey();
  return writeManifest(async () => {
    const cache = await openCache();
    if (cache) {
      try {
        await caches.delete(oldCacheName);
      } catch { }
    }
    if (supportsStorage()) {
      try {
        window.localStorage.removeItem(oldManifestKey);
      } catch { }
    }
  });
}

export async function sweepCache() {
  const generation = cacheGeneration;
  return writeManifest(async () => {
    const cache = await openCache();
    if (!cache || generation !== cacheGeneration) return;
    const pruned = await pruneManifest(loadManifest(), cache);
    if (generation === cacheGeneration) saveManifest(pruned);
  });
}

export function usePhotoVariants(image, options = {}) {
  const { variants = ['sm'], allowNetwork = true, returnObjectUrl = true } = options;
  const variantsKey = variants.join(',');
  // CDN details can arrive later without changing the content being displayed.
  // Explicit origin fallback and content edits do need a fresh request.
  const key = JSON.stringify([getPrivateCacheScope().scope, image?.id, image?.filename, getPhotoVersion(image),
    image?._force_origin, image?.origin_only, image?.force_origin, variantsKey, allowNetwork, returnObjectUrl]);
  const [state, setState] = React.useState({ key: null, urls: {} });

  React.useEffect(() => {
    if (!image || !returnObjectUrl) return;
    let cancelled = false;
    const blobUrls = new Set();
    setState({ key, urls: {} });
    for (const variant of variants) {
      getVariantUrl(image, variant, { returnObjectUrl, allowNetwork }).then(url => {
        if (!url) return;
        if (cancelled) { revokeObjectUrl(url); return; }
        if (url.startsWith('blob:')) blobUrls.add(url);
        setState(prev => ({ key, urls: { ...(prev.key === key ? prev.urls : {}), [variant]: url } }));
      }).catch(() => {});
    }
    return () => {
      cancelled = true;
      blobUrls.forEach(revokeObjectUrl);
    };
  }, [key]);

  // Native img elements own their HTTP-cache request. Avoid a second fetch and
  // avoid eager network work for thumbnails that have not scrolled into view.
  return returnObjectUrl ? (state.key === key ? state.urls : {})
    : Object.fromEntries(variants.map(variant => [variant, buildVariantUrl(image, variant)]));
}

// Synchronous check if variant is in cache manifest (for instant UI decisions)
export function isVariantCached(image, variant = 'xl') {
  if (!supportsStorage() || !image) return false;
  const stableKey = buildStableCacheKey(image, variant);
  if (!stableKey) return false;
  const signature = getPhotoVersion(image);
  const manifest = loadManifest();
  const entry = manifest.find((e) => e.stableKey === stableKey || e.cacheKey === stableKey);
  // Consider cached if entry exists and signature matches (fresh)
  return Boolean(entry && entry.signature === signature);
}

export const photoCache = {
  getVariantUrl,
  prefetchVariants,
  evictImage,
  evictByFilenames,
  clearAll: clearAllVariants,
  sweep: sweepCache,
  buildVariantUrl,
  usePhotoVariants,
  isVariantCached
};
